import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Layers3, Lightbulb, Link2, Layers, Send, Loader2, Clock, CheckCircle2, AlertCircle,
  Maximize2, X, Save, CalendarPlus, Trash2, Check,
} from "lucide-react";
import ScheduleDialog from "@/components/ScheduleDialog";
import { DeclinerPost } from "@/components/DeclinerPost";

const sources = [
  { key: "idea", label: "Une idée", icon: Lightbulb, placeholder: "Décrivez l'idée, l'anecdote ou le message à partager…" },
  { key: "link", label: "Un lien / article", icon: Link2, placeholder: "Collez le lien et/ou un résumé de l'article à commenter…" },
  { key: "pillar", label: "Un pilier", icon: Layers, placeholder: "Indiquez le pilier de contenu et l'angle souhaité…" },
];

const includeModes = [
  { key: "mix", label: "Un avec moi, un sans" },
  { key: "oui", label: "Avec mes photos" },
  { key: "non", label: "Sans moi" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// `embedded` : la page est montée dans l'onglet « Atelier » de « Créer un post ».
// Par défaut false → la route /atelier continue de fonctionner exactement comme avant.
export default function Atelier({ embedded = false }) {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [summary, setSummary] = useState({ ready: 0, working: 0, max_active: 5 });
  const [sourceType, setSourceType] = useState("idea");
  const [input, setInput] = useState("");
  const [includeMode, setIncludeMode] = useState("mix");
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [scheduleImage, setScheduleImage] = useState(null);
  const [sel, setSel] = useState({});          // { briefId: imageIndex }
  const timer = useRef(null);

  const activeSource = sources.find((s) => s.key === sourceType);
  const full = (summary.working || 0) >= (summary.max_active || 5);

  const load = async (markSeen = false) => {
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/atelier/briefs"),
        api.get("/atelier/summary"),
      ]);
      setBriefs(list);
      setSummary(sum);
      if (markSeen) {
        const unseen = list.filter((b) => b.status === "done" && !b.seen);
        await Promise.all(unseen.map((b) => api.post(`/atelier/briefs/${b.id}/seen`).catch(() => {})));
        if (unseen.length) window.dispatchEvent(new Event("atelier-refresh"));
      }
    } catch { /* silencieux : l'Atelier se rafraîchit tout seul */ }
  };

  useEffect(() => {
    load(true);
    timer.current = setInterval(() => load(false), 15000);
    return () => clearInterval(timer.current);
  }, []); // eslint-disable-line

  const submit = async () => {
    if (!input.trim()) { toast.error("Renseignez d'abord votre point de départ."); return; }
    setSending(true);
    try {
      await api.post("/atelier/briefs", { source_type: sourceType, source_input: input, include_mode: includeMode });
      setInput("");
      toast.success("Demande déposée. Vous pouvez fermer Pulse : le travail continue.");
      await load(false);
      window.dispatchEvent(new Event("atelier-refresh"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/atelier/briefs/${id}`);
      setBriefs((prev) => prev.filter((b) => b.id !== id));
      window.dispatchEvent(new Event("atelier-refresh"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveDraft = async (brief, content) => {
    const img = sel[brief.id] != null ? brief.images[sel[brief.id]]?.url : null;
    try {
      const body = { content, status: "draft", source: brief.source_type };
      if (img) { body.image_url = img; body.image_mode = "post"; }
      await api.post("/posts", body);
      toast.success(img ? "Brouillon enregistré avec son visuel." : "Brouillon enregistré dans « Mes posts ».");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const badge = (b) => {
    if (b.status === "done") return <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Prêt</span>;
    if (b.status === "error") return <span className="flex items-center gap-1.5 text-xs font-semibold text-red-600"><AlertCircle className="h-4 w-4" /> Échec</span>;
    if (b.status === "running") return <span className="flex items-center gap-1.5 text-xs font-semibold text-coral-dark"><Loader2 className="h-4 w-4 animate-spin" /> En cours</span>;
    return <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Clock className="h-4 w-4" /> En file</span>;
  };

  return (
    <div className={embedded ? "max-w-5xl" : "p-8 lg:p-12 max-w-5xl"}>
      {!embedded && (
        <>
          <div className="mb-2 flex items-center gap-2 text-coral-dark">
            <Layers3 className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Atelier</span>
          </div>
          <h1 className="font-serif text-4xl text-slate-900 mb-3">Vos demandes</h1>
        </>
      )}
      <p className="text-sm text-slate-600 mb-8 max-w-2xl">
        Déposez jusqu'à {summary.max_active} demandes, puis fermez Pulse et retournez à vos occupations.
        Vos propositions et leurs visuels vous attendront ici, conservés 30 jours.
      </p>

      {/* Dépôt d'une demande */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-8">
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          {sources.map((s) => (
            <button key={s.key} data-testid={`atelier-source-${s.key}`} onClick={() => setSourceType(s.key)}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                sourceType === s.key ? "border-coral bg-coral-strong text-white" : "border-slate-200 text-slate-600 hover:border-coral"
              }`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>
        <textarea data-testid="atelier-input" rows={4} value={input} onChange={(e) => setInput(e.target.value)}
          className="w-full border border-slate-200 rounded-sm px-3.5 py-3 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors resize-none"
          placeholder={activeSource.placeholder} />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-500">
            Visuels
            <select data-testid="atelier-include" value={includeMode} onChange={(e) => setIncludeMode(e.target.value)}
              className="border border-slate-200 rounded-sm px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-coral">
              {includeModes.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </label>
          <button data-testid="atelier-submit" onClick={submit} disabled={sending || full}
            className="bg-coral-strong text-white px-6 py-3 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60 flex items-center gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Dépôt…" : "Déposer la demande"}
          </button>
          {summary.working > 0 && (
            <span className="text-xs text-slate-500">{summary.working} demande{summary.working > 1 ? "s" : ""} en cours</span>
          )}
          {full && <span className="text-xs text-amber-700">File pleine : attendez qu'une demande se termine.</span>}
        </div>
        <p className="mt-3 text-xs text-slate-400">
          « Un avec moi, un sans » produit deux visuels de registres différents, à condition d'avoir enregistré
          vos photos dans « Ma marque ».
        </p>
      </div>

      {/* Liste des demandes */}
      <div className="space-y-6">
        {briefs.length === 0 && (
          <p className="text-sm text-slate-400">Aucune demande pour l'instant. Déposez la première ci-dessus.</p>
        )}
        {briefs.map((b) => (
          <div key={b.id} data-testid={`brief-${b.id}`} className="bg-white border border-slate-200 rounded-sm">
            <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-slate-100">
              <div className="min-w-0">
                <p className="text-sm text-slate-800 truncate">{b.source_input}</p>
                <p className="text-xs text-slate-400 mt-0.5">{fmtDate(b.created_at)}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {badge(b)}
                <button title="Supprimer" onClick={() => remove(b.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {(b.status === "queued" || b.status === "running") && (
              <div className="px-6 py-4 text-xs text-slate-400">{b.stage || "En attente…"}</div>
            )}
            {b.status === "error" && (
              <div className="px-6 py-4 text-xs text-red-600">{b.error}</div>
            )}

            {b.status === "done" && (
              <div className="px-6 py-5 space-y-5">
                {b.images_note && (
                  <p className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-px" /> {b.images_note}
                  </p>
                )}
                {b.images?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">
                      Cliquez sur un visuel pour le sélectionner (« Voir en entier » : afficher en grand).
                    </p>
                    <div className="grid grid-cols-2 gap-3 items-start">
                      {b.images.map((im, k) => (
                        <div key={k} className={`relative rounded-sm overflow-hidden border-2 transition-colors ${
                            sel[b.id] === k ? "border-coral" : "border-slate-200 hover:border-coral/60"}`}>
                          <button onClick={() => setSel((p) => ({ ...p, [b.id]: p[b.id] === k ? null : k }))}
                            className="block w-full">
                            <img src={im.url} alt={`visuel ${k + 1}`} className="w-full h-auto" />
                          </button>
                          <span className="absolute bottom-[38px] left-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-sm">
                            {im.mode === "avec" ? "Avec vous" : "Sans visage"}
                          </span>
                          <div className="flex items-stretch border-t border-slate-200 bg-white">
                            {sel[b.id] === k && (
                              <span data-testid={`atelier-image-selected-${b.id}-${k}`} aria-label="Visuel sélectionné" title="Visuel sélectionné" className="flex items-center gap-1 px-2.5 text-[11px] font-bold text-coral-dark border-r border-slate-200">
                                <Check className="h-3.5 w-3.5" /><span className="hidden sm:inline">Sélectionné</span>
                              </span>
                            )}
                            <button title="Voir en entier" aria-label="Voir le visuel en entier" data-testid={`atelier-image-zoom-${b.id}-${k}`}
                              onClick={(e) => { e.stopPropagation(); setLightbox(im.url); }}
                              className="flex-1 flex items-center justify-center gap-1.5 text-slate-600 text-xs font-semibold py-1.5 hover:text-coral-dark hover:bg-slate-50 transition-colors">
                              <Maximize2 className="h-3.5 w-3.5" /><span className="hidden sm:inline">Voir en entier</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {b.proposals?.map((p, i) => (
                  <div key={i} className="border border-slate-100 rounded-sm">
                    <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-[0.15em] text-coral-dark">Proposition {i + 1}</span>
                      <span className={`text-xs ${p.length > 3000 ? "font-bold text-red-600" : "text-slate-400"}`}>
                        {p.length} / 3000 caractères{p.length > 3000 ? " : trop long pour LinkedIn" : ""}
                      </span>
                    </div>
                    <p className="px-4 py-3 font-serif text-[15px] leading-[1.7] text-slate-800 whitespace-pre-wrap">{p}</p>
                    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100">
                      <button onClick={() => saveDraft(b, p)}
                        className="flex items-center gap-2 text-sm font-semibold text-coral-dark border border-coral px-4 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors">
                        <Save className="h-4 w-4" /> Brouillon{sel[b.id] != null ? " + visuel" : ""}
                      </button>
                      <button onClick={() => {
                          setScheduleImage(sel[b.id] != null ? b.images[sel[b.id]]?.url : null);
                          setScheduleFor(p);
                        }}
                        className="flex items-center gap-2 text-sm font-semibold text-white bg-coral-strong px-4 py-2 rounded-sm hover:bg-coral-hover transition-colors">
                        <CalendarPlus className="h-4 w-4" /> Planifier{sel[b.id] != null ? " + visuel" : ""}
                      </button>
                    </div>
                    <div className="px-4 pb-3">
                      <DeclinerPost postId={null} context="atelier" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="visuel en grand" className="max-h-[92vh] max-w-[92vw] object-contain rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()} />
          <button aria-label="Fermer" onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/90 hover:text-white">
            <X className="h-7 w-7" />
          </button>
        </div>
      )}

      {scheduleFor !== null && (
        <ScheduleDialog
          content={scheduleFor}
          source="atelier"
          initialImage={scheduleImage}
          initialImageMeta={scheduleImage ? { image_mode: "post" } : null}
          onClose={() => { setScheduleFor(null); setScheduleImage(null); }}
          onScheduled={() => { setScheduleFor(null); setScheduleImage(null); toast.success("Post ajouté à la file de publication."); }}
        />
      )}
    </div>
  );
}
